import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Gameboard from "../gameboard";
import CardScope, { useCardScope } from "./index";
import CardBusy from "./CardBusy";
import { yetiBucketForWindow } from "../../hooks/company";
import useLeaderboardData from "../LeaderBoard/LeaderboardTable/data";
import "./cardScope.css";

/**
 * King of the Mountain, scopeable — it can combine workspaces and follow the
 * board's Viewing filter like every other card.
 *
 * Why its own wrapper instead of a ScopedCard render prop: Gameboard ANIMATES.
 * It climbs from zero to the score and restarts that climb whenever its score
 * props change. Handed the live scoped value straight through, every data tick
 * (loading → 0 → workspace A → workspace B) restarted the climb and the peak
 * sat at zero. So the value is SETTLED here first — held in state, updated only
 * when the numbers have actually resolved to something new — then handed to a
 * memoised Gameboard that only rebuilds when the settled value changes. The
 * climb plays once, to the right height.
 *
 * Single-workspace boards score team + Yeti through useLeaderboardData — the
 * SAME hook as LiveLeaderboard — so the mountain cannot disagree with Bobby /
 * The Yeti on the list. Multi-workspace keeps useScopedMetrics.
 */

/** Windows that must override Gameboard's legacy UseYetiStats path. */
const NEEDS_SCOPED_WINDOW = new Set([
  "custom", "quarter", "ytd", "year",
  "today", "yesterday", "week", "lastWeek", "lastMonth", "month",
]);

const avgScorerPts = (rows) => {
  const scorers = (rows || []).filter((r) => (Number(r.pts) || 0) > 0);
  if (!scorers.length) return 0;
  const sum = scorers.reduce((n, r) => n + (Number(r.pts) || 0), 0);
  return Math.round(sum / scorers.length);
};

const ScopedGameboard = ({ settings, ownWorkspaceId, editing, onSettings, scene, title, boardWs, boardWindow, boardCustom, inheritWorkspaceIds }) => {
  const scope = useCardScope(settings, ownWorkspaceId, boardWs, { window: boardWindow, custom: boardCustom }, inheritWorkspaceIds);
  const { pick } = scope;
  const setPick = (v) => {
    scope.choose(v);
    if (onSettings) onSettings({ ...settings, cardPick: v });
  };

  /* The card only overrides Gameboard's own numbers when it is actually scoped
     to one or more workspaces (workspaceIds set). An unscoped Mountain keeps
     doing exactly what it always did — Gameboard's own average for the board's
     workspace — so nothing changes for a plain board. */
  const isScoped = ((settings && settings.workspaceIds) || []).length > 0
    || ((inheritWorkspaceIds || []).filter(Boolean).length >= 2);
  /* The window this Mountain is actually on — same Match/Follow rule as
     every other scoped card (resolveCardWindow inside useCardScope). */
  const effWin = scope.window || boardWindow || "month";
  const boardBucket = effWin === "year" ? "ytd" : effWin;
  const customKey = scope.custom
    ? `${scope.custom.from || ""}~${scope.custom.to || ""}`
    : "";
  /* Named buckets Gameboard still understands natively. Custom / quarter / YTD
     must NEVER fall through to periodIdx=3 (This month) — that was the custom-
     range bug showing a full month climb. */
  const WIN_TO_PERIOD_IDX = {
    yesterday: 0, today: 1, week: 2, month: 3, lastWeek: 4, lastMonth: 5,
  };
  const periodIdx = WIN_TO_PERIOD_IDX[effWin] ?? 3;
  const useScoped = isScoped || NEEDS_SCOPED_WINDOW.has(effWin);

  /* One workspace → same rows/yeti as the ProgressBar list. Empty ids when
     multi-ws so the hook is a no-op and we keep useScopedMetrics averages. */
  const readingKey = (scope.reading || []).map(String).filter(Boolean).join("|");
  const boardIds = useMemo(() => {
    if (!useScoped) return [];
    const ids = readingKey ? readingKey.split("|") : [];
    return ids.length === 1 ? ids : [];
  }, [useScoped, readingKey]);
  const boardRange = boardBucket === "custom" ? (scope.custom || null) : null;
  const {
    rows: boardRows,
    yetiPts: boardYetiPts,
    loading: boardLoading,
  } = useLeaderboardData(boardIds, boardBucket || "month", null, boardRange);
  const useBoardScores = boardIds.length === 1;

  const scopeTeamPts = Math.round(Number(scope.teamPts) || 0);
  /* useScopedMetrics yeti: today/yesterday stay named; week/lastWeek/custom/
     quarter/YTD use the range-paced `custom` bucket (same as the leaderboard). */
  const yetiBucket = yetiBucketForWindow(effWin === "year" ? "ytd" : effWin);
  const scopeYetiPts = Math.round((() => {
    const period = scope.yeti?.[yetiBucket] || scope.yeti?.custom || scope.yeti?.month;
    if (!period || typeof period !== "object") return 0;
    if (period.total != null) {
      return Number(period.total?.pts ?? period.total) || 0;
    }
    return Object.values(period).reduce((sum, v) => {
      if (v == null) return sum;
      if (typeof v === "number") return sum + v;
      return sum + (Number(v.pts) || 0);
    }, 0);
  })());

  const teamPts = useBoardScores ? avgScorerPts(boardRows) : scopeTeamPts;
  const yetiPts = useBoardScores
    ? Math.round(Number(boardYetiPts) || 0)
    : scopeYetiPts;
  /* PhotoMountain returns null when maxPoint <= 0. On an empty host that
     inherits other workspaces, yeti.maxTotal can briefly (or wrongly) be 0
     even when teamPts is real — floor so the climb still paints. */
  const maxPoint = Math.round(Number(scope.yeti?.maxTotal) || 0)
    || Math.max(teamPts, yetiPts, 1);

  const [settled, setSettled] = useState(null);
  const scoresLoading = scope.loading || (useBoardScores && boardLoading);

  /* King of the Mountain paints its whole scene from a light/dark palette gated
     on `darkMode`. On the TV the board sits inside `.tv-board.tv-dark`, so detect
     that ancestor and switch the Mountain to its dark palette — otherwise it
     renders the light sky + dark text on the dark TV. No `.tv-dark` ancestor on
     the home page, so it stays light exactly as before. */
  const rootRef = useRef(null);
  const [darkMode, setDarkMode] = useState(false);
  useLayoutEffect(() => {
    setDarkMode(!!(rootRef.current && rootRef.current.closest(".tv-dark")));
  }, []);

  /* Drop the previous window's climb the instant the filter changes — otherwise
     Sep 1–4 briefly (or forever, if settle stalls) keeps showing the month. */
  useEffect(() => {
    setSettled(null);
  }, [effWin, customKey, useScoped, readingKey]);

  useEffect(() => {
    if (!useScoped || scoresLoading) return;
    /* Always settle once scoring finishes — including zeros. Waiting for a
       non-zero left `settled` null forever on an empty host (Head of Sales). */
    setSettled((prev) => (
      prev && prev.teamPts === teamPts && prev.yetiPts === yetiPts && prev.maxPoint === maxPoint
        ? prev
        : { teamPts, yetiPts, maxPoint }
    ));
  }, [useScoped, scoresLoading, teamPts, yetiPts, maxPoint]);

  /* Only override Gameboard's own numbers while the scoped value is in play. Back
     on the default This Month of a plain board, hand nothing over so Gameboard
     shows exactly what it always did. */
  const override = useScoped ? settled : null;
  const waiting = useScoped && (scoresLoading || !settled);
  const board = useMemo(() => {
    if (waiting) {
      return (
        <div className="cs-wrap" style={{ minHeight: 280 }}>
          <CardBusy active cover label="Loading mountain…" />
          <div className="cs-emptyframe" aria-hidden>
            <div className="cs-emptyframe-h" />
            <div className="cs-empty" />
          </div>
        </div>
      );
    }
    return (
      <Gameboard
        noScroll
        peakTrophy
        darkMode={darkMode}
        type={scene === "photo" ? "photo" : undefined}
        title={title || undefined}
        playerScore={override ? override.teamPts : undefined}
        yetiScore={override ? override.yetiPts : undefined}
        maxPoint={override && override.maxPoint ? override.maxPoint : undefined}
        periodIdx={periodIdx}
      />
    );
  }, [scene, title, override, waiting, periodIdx, darkMode]);

  /* Soft veil when the board window changes (quarter / YTD) while settled
     still shows the previous climb — otherwise it looks frozen mid-update. */
  const softBusy = useScoped && !!scope.refreshing && !waiting;

  /* The Mountain never carries its own workspace dropdown: on a cross-workspace
     board the navbar Viewing filter owns the choice; on a plain board there is
     nothing to switch. gmbw-fill keeps the card the full height of its cell.
     While editing a single-workspace scoped card, a dashed ghost mirrors the
     other cards' "Needs 2+ workspaces" hint. */
  const showGhost = editing && !scope.navControlled && scope.inScope.length === 1;
  return (
    <div ref={rootRef} className={`gmbw-fill cs-wrap${softBusy ? " cs-is-refreshing" : ""}`} style={{ position: "relative" }}>
      {showGhost && <CardScope scope={{ ...scope, pick }} onPick={setPick} editing />}
      <CardBusy active={softBusy} label="Updating for date range…" />
      {board}
    </div>
  );
};

ScopedGameboard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.object,
  ownWorkspaceId: PropTypes.string,
  editing: PropTypes.bool,
  onSettings: PropTypes.func,
  scene: PropTypes.string,
  title: PropTypes.string,
  boardWs: PropTypes.string,
  boardWindow: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  boardCustom: PropTypes.object,
  inheritWorkspaceIds: PropTypes.arrayOf(PropTypes.string),
};
ScopedGameboard.defaultProps = {
  settings: {},
  ownWorkspaceId: null,
  editing: false,
  onSettings: null,
  scene: "photo",
  title: undefined,
  boardWs: null,
  boardWindow: null,
  boardCustom: null,
  inheritWorkspaceIds: [],
};

export default ScopedGameboard;
